import Konva from 'konva'
import { useEffect, useMemo, useRef } from 'react'
import { Circle, Group } from 'react-konva'
import { useMapStore } from '@/stores/mapStore'
import { useNavigationStore } from '@/stores/navigationStore'
import { useStepNavigationStore } from '@/stores/stepNavigationStore'
import { getMapCoordinates } from './mapCoordinates'

interface RouteTrailProps {
  points: [number, number][]
}

const DOT_SPACING = 32
const WAYPOINT_RADIUS = 8
const DOT_RADIUS = 6
const TRAIL_FILL = '#1E22AA'
const STROKE_WIDTH = 2.5
const FILL_DURATION = 0.2
const FILL_STAGGER = 0.035

type RouteCircle = {
  x: number
  y: number
  radius: number
  isPassed: boolean
  positionInSegment: number
  key: string
}

type RouteTrailCircleProps = {
  circle: RouteCircle
  isRouteActive: boolean
}

const RouteTrailCircle = ({ circle, isRouteActive }: RouteTrailCircleProps) => {
  const nodeRef = useRef<Konva.Circle>(null)
  const wasPassedRef = useRef(false)
  const pulseRef = useRef<Konva.Animation>()

  useEffect(() => {
    const node = nodeRef.current
    if (!node) return

    pulseRef.current?.stop()
    pulseRef.current = undefined

    if (!circle.isPassed) {
      node.fill('transparent')
      node.opacity(1)
      wasPassedRef.current = false

      if (!isRouteActive) return

      const layer = node.getLayer()
      if (!layer) return

      const pulse = new Konva.Animation((frame) => {
        if (!frame) return
        node.opacity(0.45 + 0.35 * Math.sin(frame.time / 420))
      }, layer)

      pulseRef.current = pulse
      pulse.start()

      return () => {
        pulse.stop()
      }
    }

    pulseRef.current?.stop()
    pulseRef.current = undefined

    if (!isRouteActive) {
      node.fill(TRAIL_FILL)
      node.opacity(1)
      wasPassedRef.current = true
      return
    }

    if (wasPassedRef.current) {
      node.fill(TRAIL_FILL)
      node.opacity(1)
      return
    }

    wasPassedRef.current = true
    node.fill('transparent')
    node.opacity(1)
    node.to({
      fill: TRAIL_FILL,
      duration: FILL_DURATION,
      delay: circle.positionInSegment * FILL_STAGGER,
      easing: Konva.Easings.EaseOut,
      onFinish: () => {
        node.fill(TRAIL_FILL)
      },
    })

  }, [circle.isPassed, circle.positionInSegment, isRouteActive])

  return (
    <Circle
      ref={nodeRef}
      x={circle.x}
      y={circle.y}
      radius={circle.radius}
      fill="transparent"
      stroke={TRAIL_FILL}
      strokeWidth={STROKE_WIDTH}
    />
  )
}

export const RouteTrail = ({ points }: RouteTrailProps) => {
  const [width, height] = useMapStore((state) => state.size)
  const currentStep = useStepNavigationStore((state) => state.currentStep)
  const isRouteActive = useNavigationStore((state) => state.isRouteActive)

  const circles = useMemo(() => {
    const mappedPoints = points.map(([x, y]) => ({
      x: getMapCoordinates(width, x),
      y: getMapCoordinates(height, y),
    }))

    const result: RouteCircle[] = []

    mappedPoints.forEach((point, index) => {
      result.push({
        x: point.x,
        y: point.y,
        radius: WAYPOINT_RADIUS,
        isPassed: index < currentStep,
        positionInSegment: 0,
        key: `waypoint-${index}`,
      })

      if (index >= mappedPoints.length - 1) return

      const a = mappedPoints[index]
      const b = mappedPoints[index + 1]
      const dx = b.x - a.x
      const dy = b.y - a.y
      const length = Math.hypot(dx, dy)
      const count = Math.floor(length / DOT_SPACING)
      const isPassed = index < currentStep

      for (let p = 1; p < count; p++) {
        const ratio = p / count
        result.push({
          x: a.x + dx * ratio,
          y: a.y + dy * ratio,
          radius: DOT_RADIUS,
          isPassed,
          positionInSegment: p,
          key: `${index}-${p}`,
        })
      }
    })

    return result
  }, [points, width, height, currentStep])

  return (
    <Group>
      {circles.map((circle) => (
        <RouteTrailCircle
          key={circle.key}
          circle={circle}
          isRouteActive={isRouteActive}
        />
      ))}
    </Group>
  )
}
